import { flow, toPairs } from "lodash-es";
import type { UUID } from "../../../utils/uuid";

const EMAIL_REG_EXP =
  /(?<=<(a|mj-button|v:\w+)(?:[^>]*?)\s+href=")(https?:\/\/(?:(?!{{#jwt}})[^\s">]|{{#jwt}}[\s\S]*?{{\/jwt}})+)/g;

// Matches raw links inside angle brackets, stopping before | or >
const CHAT_LINK_REG_EXP = /(?<=<)(https?:\/\/[^|>]+)/g;

const BASE_URL_PATTERN = "https?://[\\wÀ-ÿ./?&\\{\\}$=#@;:%,_~+!*'()\\-]+";

// Matches raw links not already inside markdown-like formatting
const CHAT_RAW_LINK_REG_EXP = new RegExp(
  `\\b(?<!(<[^|>]*\\|[^>]*))(${BASE_URL_PATTERN}[\\wÀ-ÿ/\\}])`,
  "g",
);

// Matches markdown-style [text](link) for Teams
const TEAMS_LINK_REG_EXP = new RegExp(`(?<=\\[[^\\]]+\\]\\()(${BASE_URL_PATTERN})(?=\\))`, "g");

// Matches raw links not already part of markdown in Teams
const TEAMS_RAW_LINK_REG_EXP = new RegExp(
  `\\b(?<!\\[[^\\]]*?)(?<!\\]\\([^)]+)(${BASE_URL_PATTERN}[\\wÀ-ÿ/\\}])`,
  "g",
);

// Matches any Twilio-style URL and more specifically those with JWT defined.
const TWILIO_LINK_REG_EXP = new RegExp(
  `\\b${BASE_URL_PATTERN}{{#jwt}}.*?{{/jwt}}|\\b${BASE_URL_PATTERN}[\\wÀ-ÿ/\\}]`,
  "g",
);

// Simple matcher for URLs anywhere
const SIMPLE_LINK_REG_EXP = new RegExp(`${BASE_URL_PATTERN}[\\wÀ-ÿ/\\}]`, "g");

export type ReplaceInfo = { body: string; replaces: { [x: number]: string } };

type ReplaceInfoWithIds = {
  body: string;
  replaces: { [x: number]: { original: string; id: UUID } };
};

export function extractEmailLinks(text: string): ReplaceInfo {
  return extractLinks(text, EMAIL_REG_EXP, "email_hyperlink");
}

type ExtractSlackLinkMode = "rich_text" | "mrkdwn";

export function extractSlackLinks(
  text: string,
  mode: ExtractSlackLinkMode = "mrkdwn",
): ReplaceInfo {
  const linkedInfo = extractLinks(text, CHAT_LINK_REG_EXP, "slack_hyperlink");
  const chatInfo = extractLinks(
    linkedInfo.body,
    CHAT_RAW_LINK_REG_EXP,
    mode === "mrkdwn" ? "slack_raw_hyperlink" : "slack_hyperlink",
  );

  return { body: chatInfo.body, replaces: { ...linkedInfo.replaces, ...chatInfo.replaces } };
}

export function extractTeamsLinks(text: string): ReplaceInfo {
  const linkedInfo = extractLinks(text, TEAMS_LINK_REG_EXP, "teams_hyperlink");
  const chatInfo = extractLinks(linkedInfo.body, TEAMS_RAW_LINK_REG_EXP, "teams_raw_hyperlink");
  return { body: chatInfo.body, replaces: { ...linkedInfo.replaces, ...chatInfo.replaces } };
}

export function extractTwilioLinks(text: string): ReplaceInfo {
  // const regex = new RegExp(TWILIO_LINK_REG_EXP.source + "|" + SURVEY_JWT_LINK_REG_EXP.source, "g");
  return extractLinks(text, TWILIO_LINK_REG_EXP, "twilio_hyperlink");
}

export function extractSimpleLink(text: string): ReplaceInfo {
  return extractLinks(text, SIMPLE_LINK_REG_EXP, "simple_hyperlink");
}

export function thisIsALinkJustDoIt(link: string): ReplaceInfo {
  return extractLinks(link, /.*/, "link_hyperlink");
}

export function replaceLinksWithHyperlinks(info: ReplaceInfoWithIds): string {
  const replacers = {
    replace: (link: string, id: UUID): string => `{{hyperlink:${id}}}`,
    replaceWithSlackLink: (link: string, id: UUID): string => `<{{hyperlink:${id}}}|${link}>`,
    replaceWithMarkdownLink: (link: string, id: UUID): string => `[${link}]({{hyperlink:${id}}})`,
  };

  const transform: (whatever: string) => string = flow(
    toPairs(info.replaces).map(([replaceKey, { original, id }]) => {
      const mode = replaceKey.includes("slack_raw_hyperlink")
        ? "replaceWithSlackLink"
        : replaceKey.includes("teams_raw_hyperlink")
          ? "replaceWithMarkdownLink"
          : "replace";
      return (x: string) => x.replaceAll(replaceKey, replacers[mode](original, id));
    }),
  );

  return transform(info.body);
}

function extractLinks(text: string, regEx: RegExp, placeholder: string): ReplaceInfo {
  const replaces: { [x: string]: string } = {};

  let i = 0;
  const body = text.replace(regEx, (m) => {
    const key = `{{${placeholder}:${i++}}}`;
    replaces[key] = m.replaceAll("&amp;", "&").replaceAll("&quot;", '"');
    return key;
  });

  return { body, replaces };
}
